import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Bookmark, Coins, Crown, ExternalLink, Eye, Gift, Globe, Plus, Video } from "lucide-react";
import { API } from "../config/api";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ClapIcon } from "../lib/icons";
import MyVideosTab from "../components/dashboard/MyVideosTab";
import VideoRenewalDialog from "../components/dashboard/VideoRenewalDialog";
import SubscriptionTierCard from "../components/dashboard/SubscriptionTierCard";
import SubscriptionSuccessModal from "../components/dashboard/SubscriptionSuccessModal";
import ChannelChatTab from "../components/dashboard/ChannelChatTab";
import TransactionsTab from "../components/dashboard/TransactionsTab";
import ProducerPulse from "../components/dashboard/ProducerPulse";
import ReferralStatsCard from "../components/dashboard/ReferralStatsCard";
import VideoRecorderDialog from "../components/dashboard/VideoRecorderDialog";
import ProfileTab from "../components/dashboard/ProfileTab";
import ChannelsTab from "../components/dashboard/ChannelsTab";
import OffersTab from "../components/dashboard/OffersTab";
import SecurityTab from "../components/dashboard/SecurityTab";
import TopSavedLeaderboard from "../components/dashboard/TopSavedLeaderboard";
import XFollowPanel from "../components/dashboard/XFollowPanel";
import XMilestoneCelebrate from "../components/dashboard/XMilestoneCelebrate";
import DashboardRecentFollowers from "./dashboard/DashboardRecentFollowers";
import { usePaymentRedirectHandler } from "./dashboard/usePaymentRedirectHandler";

// DashboardPage — page shell. Heavy lifters (recorder, profile, tabs) live in
// /components/dashboard. This file owns: page-level effects (URL params,
// commission acceptance, channel chat, video CRUD handlers) and tab layout.
// Payment-redirect handling lives in `dashboard/usePaymentRedirectHandler.js`;
// recent followers panel + follow-back live in `dashboard/DashboardRecentFollowers.jsx`.

const DashboardPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [transactions, setTransactions] = useState([]);
  // Loading flag retained for future progressive UX; kept underscored so ESLint
  // recognizes the deliberate ignore on initial mount.
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [showCreateVideo, setShowCreateVideo] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoComments, setVideoComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [channelChatMessages, setChannelChatMessages] = useState([]);
  const [loadingChannelChat, setLoadingChannelChat] = useState(false);
  // X self-share — tracks which of the producer's videos have already
  // earned today's +1 bonus, plus how many shares remain for the day.
  // Hydrated once on mount via /x/share/self/today; updated optimistically
  // inside the per-video share handler so the UI never disagrees with the
  // server.
  const [selfShareUsedToday, setSelfShareUsedToday] = useState(() => new Set());
  const [selfShareRemaining, setSelfShareRemaining] = useState(5);
  // Renewal dialog state — `renewingVideo` truthy = modal open. The
  // dialog deep-link from the email nudge sets this via the ?renew=
  // URL param effect below.
  const [renewingVideo, setRenewingVideo] = useState(null);
  const navigate = useNavigate();
  const [dashboardSearchParams, setDashboardSearchParams] = useSearchParams();

  // Both Stripe redirect-back paths (legacy one-time + recurring) +
  // the ?payment_cancelled toast — bundled into one hook.
  usePaymentRedirectHandler({ dashboardSearchParams, setDashboardSearchParams, token, refreshUser });

  // Auto-open the recorder when navigated with ?record=1 (global Record FAB)
  useEffect(() => {
    if (dashboardSearchParams.get("record") === "1") {
      setShowCreateVideo(true);
      dashboardSearchParams.delete("record");
      setDashboardSearchParams(dashboardSearchParams, { replace: true });
    }
  }, [dashboardSearchParams, setDashboardSearchParams]);

  // Commission acceptance: ?commission=<id> opens the recorder pre-filled with
  // the EP's spec (locked) and routes the publish through commissions/submit.
  const [activeCommission, setActiveCommission] = useState(null);
  const [commissionLockUntil, setCommissionLockUntil] = useState(null);
  useEffect(() => {
    const cid = dashboardSearchParams.get("commission");
    if (!cid || activeCommission?.id === cid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/commissions/${cid}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {});
        if (cancelled) return;
        setActiveCommission(res.data);
        const lockRes = await api.post(`/commissions/${cid}/lock`, {});
        if (cancelled) return;
        setCommissionLockUntil(lockRes.data?.locked_until || null);
        setShowCreateVideo(true);
        toast.success(`Commission accepted — you have ${lockRes.data?.minutes || 15} min to film & publish`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Couldn't accept this commission");
        dashboardSearchParams.delete("commission");
        setDashboardSearchParams(dashboardSearchParams, { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearchParams, token]);

  const clearActiveCommission = () => {
    setActiveCommission(null);
    setCommissionLockUntil(null);
    if (dashboardSearchParams.get("commission")) {
      dashboardSearchParams.delete("commission");
      setDashboardSearchParams(dashboardSearchParams, { replace: true });
    }
  };

  // Initial fetch — videos + transactions
  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    const fetchData = async () => {
      try {
        const [videosRes, transRes] = await Promise.all([
          api.get(`/users/${user.username}/videos`),
          api.get(`/transactions`),
        ]);
        setVideos(videosRes.data);
        setTransactions(transRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [user, token, navigate]);

  // X self-share daily status — fetched once per dashboard mount.
  // The endpoint is auth-only; on 401/network we silently leave defaults
  // (5 remaining, empty used-set) so the UI degrades gracefully.
  useEffect(() => {
    if (!user) return;
    const isProducer = (user.subscription_tier || "free").toLowerCase() === "producer";
    if (!isProducer) return;
    api.get("/x/share/self/today")
      .then((r) => {
        setSelfShareUsedToday(new Set(r.data?.used_video_ids || []));
        setSelfShareRemaining(r.data?.remaining_today ?? 5);
      })
      .catch(() => { /* tolerate */ });
  }, [user]);

  // Called by XSelfShareButton after a successful self-share so the
  // sibling buttons see the new state without re-fetching.
  const handleSelfShared = (videoId, _reward, remainingAfter) => {
    setSelfShareUsedToday((prev) => {
      const next = new Set(prev);
      next.add(videoId);
      return next;
    });
    setSelfShareRemaining(typeof remainingAfter === "number" ? remainingAfter : (selfShareRemaining - 1));
    refreshUser?.();
  };

  // Listen for an "open-recorder" event so any component (e.g. the
  // SubscriptionSuccessModal's CTA) can pop the video recorder dialog
  // without needing prop drilling through the layout.
  useEffect(() => {
    const handler = () => setShowCreateVideo(true);
    window.addEventListener("open-recorder", handler);
    return () => window.removeEventListener("open-recorder", handler);
  }, []);

  // Channel chat
  useEffect(() => {
    const fetchChannelChat = async () => {
      if (user) {
        setLoadingChannelChat(true);
        try {
          const res = await api.get(`/dashboard/channel-chat`);
          setChannelChatMessages(res.data);
        } catch (err) {
          console.error("Failed to fetch channel chat:", err);
          setChannelChatMessages([]);
        }
        setLoadingChannelChat(false);
      }
    };
    fetchChannelChat();
  }, [user, token]);

  // Comments for the selected video
  useEffect(() => {
    const fetchComments = async () => {
      if (selectedVideo) {
        setLoadingComments(true);
        try {
          const res = await api.get(`/videos/${selectedVideo.id}/comments`);
          setVideoComments(res.data);
        } catch (err) {
          console.error(err);
          setVideoComments([]);
        }
        setLoadingComments(false);
      }
    };
    fetchComments();
  }, [selectedVideo]);

  // Video management handlers
  const handleToggleHighlight = async (video) => {
    try {
      const res = await api.put(`/videos/${video.id}/highlight`, {});
      setVideos(videos.map(v => v.id === video.id ? { ...v, highlighted: res.data.highlighted } : v));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update highlight");
    }
  };

  const handleDeleteVideo = async (video) => {
    if (!window.confirm(`Are you sure you want to delete "${video.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/videos/${video.id}`);
      setVideos(videos.filter(v => v.id !== video.id));
      setSelectedVideo(null);
      toast.success("Video deleted successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete video");
    }
  };

  // Renewal — surface the dialog. The dialog itself POSTs; we only
  // need to merge the new expires_at into the local list on success.
  const handleRenewVideo = (video) => setRenewingVideo(video);
  const handleRenewed = ({ videoId, expires_at, storage_days, auto_renew, new_balance }) => {
    setVideos(prev => prev.map(v => {
      if (v.id !== videoId) return v;
      const next = { ...v };
      if (expires_at !== undefined) next.expires_at = expires_at;
      if (storage_days !== undefined) next.storage_days = storage_days;
      if (auto_renew !== undefined) next.auto_renew = auto_renew;
      if (expires_at !== undefined) next.is_deleted = false;
      return next;
    }));
    if (typeof new_balance === 'number') {
      refreshUser?.();
    }
  };

  // Deep-link from the renewal nudge email lands at
  // /dashboard?tab=videos&renew=<video_id>. Auto-open the modal once
  // the videos list is hydrated.
  useEffect(() => {
    const renewId = dashboardSearchParams.get('renew');
    if (!renewId || !videos.length) return;
    const v = videos.find(x => x.id === renewId);
    if (v) {
      setRenewingVideo(v);
      // Strip the param so a refresh doesn't re-open the modal.
      const next = new URLSearchParams(dashboardSearchParams);
      next.delete('renew');
      setDashboardSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos.length]);

  const handleDeleteComment = async (commentId) => {
    if (!selectedVideo) return;
    try {
      await api.delete(`/videos/${selectedVideo.id}/comments/${commentId}/owner`);
      setVideoComments(videoComments.filter(c => c.id !== commentId));
      toast.success("Comment deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete comment");
    }
  };

  const handleDeleteChannelChat = async (messageId) => {
    try {
      await api.delete(`/channel-chat/${messageId}`);
      setChannelChatMessages(channelChatMessages.filter(m => m.id !== messageId));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete message");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black py-4 sm:py-8" data-testid="dashboard-page">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Dashboard</h1>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            data-testid="create-video-btn"
            onClick={() => setShowCreateVideo(true)}
          >
            <Plus className="h-4 w-4 mr-2" /> Create Video
          </Button>
        </div>

        <VideoRecorderDialog
          open={showCreateVideo}
          onOpenChange={setShowCreateVideo}
          user={user}
          token={token}
          refreshUser={refreshUser}
          activeCommission={activeCommission}
          commissionLockUntil={commissionLockUntil}
          onCommissionDone={clearActiveCommission}
          onVideoCreated={(newVideo) => setVideos(prev => [newVideo, ...prev])}
        />

        {/* Subscription tier card — first thing users see post-checkout
            so they always know what they bought and what's next. */}
        <SubscriptionTierCard user={user} />

        {/* Recent Followers */}
        <DashboardRecentFollowers user={user} token={token} />

        {/* Producer Pulse — 7-day sparkline trio (auto-hides when empty) */}
        <ProducerPulse />

        {/* Referrals — profile-as-referral-link score for the last 30
            days. Auto-hides for brand-new users with a zero score so
            their first dashboard doesn't feel like a scoreboard of
            failures. */}
        <ReferralStatsCard />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8" data-testid="dashboard-stats-row">
          <Card className="bg-gray-900 border-gray-800" data-testid="stats-card-credits">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm">Credits</p>
                  <p className="text-lg sm:text-2xl font-bold text-emerald-400" data-testid="dashboard-credits">{user.credits.toFixed(2)}</p>
                  <Link to="/pricing"
                    className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-emerald-400/80 hover:text-emerald-300 mt-1 transition-colors"
                    data-testid="dashboard-buy-credits-link">
                    <Plus className="h-3 w-3" /> Buy more
                  </Link>
                </div>
                <Coins className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800" data-testid="stats-card-videos">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm">Videos</p>
                  <p className="text-lg sm:text-2xl font-bold text-white" data-testid="dashboard-videos-count">{videos.length}</p>
                </div>
                <Video className="h-6 w-6 sm:h-8 sm:w-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800" data-testid="stats-card-views">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm">Views</p>
                  <p className="text-lg sm:text-2xl font-bold text-white" data-testid="dashboard-views-count">{videos.reduce((sum, v) => sum + (v.loop_count || 0), 0)}</p>
                </div>
                <Eye className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800" data-testid="stats-card-applauds">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm">Applauds</p>
                  <p className="text-lg sm:text-2xl font-bold text-pink-400" data-testid="dashboard-applauds-count">{videos.reduce((sum, v) => sum + v.applaud_count, 0)}</p>
                </div>
                <ClapIcon className="h-6 w-6 sm:h-8 sm:w-8 text-pink-400" />
              </div>
            </CardContent>
          </Card>
          {/* Saved-to-Trail aggregate. Surfaces the social-proof signal
              that completes the loop: viewers bookmark → producers see
              it. Hidden when zero saves to avoid demotivating new
              producers with a sea of zeros. */}
          {(() => {
            const totalSaves = videos.reduce((sum, v) => sum + (v.trail_count || 0), 0);
            return (
              <Card className="bg-gray-900 border-gray-800" data-testid="stats-card-saves">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-xs sm:text-sm">Saved</p>
                      <p className="text-lg sm:text-2xl font-bold text-amber-400" data-testid="dashboard-saves-count">{totalSaves}</p>
                      <p className="text-[10px] sm:text-[11px] text-gray-500 mt-1">to viewers' Trails</p>
                    </div>
                    <Bookmark className="h-6 w-6 sm:h-8 sm:w-8 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Producer's top-3 most-saved videos — completes the
            engagement loop. Hidden when no video has been saved yet. */}
        <TopSavedLeaderboard videos={videos} />

        {/* Milestone celebration banner — surfaces the highest pending
            (uncelebrated) loop milestone for one-tap X sharing. Same
            +1 credit reward as the per-video share button, but the
            tweet text is the punchy celebration line. Self-hides
            when no pending milestones remain. */}
        <XMilestoneCelebrate
          isProducer={(user.subscription_tier || "free").toLowerCase() === "producer"}
          refreshUser={refreshUser}
        />

        {/* X "Follow & Earn" loop — Producers only. Hides itself
            silently for non-Producers or when X isn't configured. The
            ?x_prompt=1 query (set right after a Producer subscription
            success) auto-opens the list picker. */}
        <XFollowPanel
          user={user}
          refreshUser={refreshUser}
          autoOpenPicker={dashboardSearchParams.get("x_prompt") === "1"}
        />

        {/* Gifts feature card */}
        <Link to="/gifts" data-testid="dashboard-gifts-card" className="block mb-6 sm:mb-8 group">
          <div className="rounded-xl border border-pink-500/30 bg-gradient-to-r from-pink-950/40 via-rose-950/30 to-emerald-950/30 p-4 sm:p-5 hover:border-pink-400/60 transition-colors">
            <div className="flex items-center gap-4">
              <div className="bg-pink-600/20 border border-pink-500/40 rounded-lg p-2 sm:p-3 shrink-0">
                <Gift className="h-6 w-6 sm:h-8 sm:w-8 text-pink-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-base sm:text-lg">Gift credits — or invite friends &amp; family to gift you</p>
                <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                  Send 100 credits for $4 via magic link, or share your public wishlist ($4 / $8 / $20 / $40 / custom).
                </p>
              </div>
              <span className="text-pink-300 text-sm font-medium hidden sm:inline">Open Gifts →</span>
            </div>
          </div>
        </Link>

        <Tabs
          defaultValue={dashboardSearchParams.get("tab") || "videos"}
          className="space-y-4 sm:space-y-6"
        >
          <TabsList className="bg-gray-900 border border-gray-800 w-full sm:w-auto flex overflow-x-auto whitespace-nowrap">
            <TabsTrigger value="videos" data-testid="tab-my-videos" className="data-[state=active]:bg-emerald-600 flex-1 sm:flex-none text-xs sm:text-sm">My Videos</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-channel-chat" className="data-[state=active]:bg-yellow-600 flex-1 sm:flex-none text-xs sm:text-sm">Channel Chat</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-history" className="data-[state=active]:bg-emerald-600 flex-1 sm:flex-none text-xs sm:text-sm">History</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-emerald-600 flex-1 sm:flex-none text-xs sm:text-sm">Profile</TabsTrigger>
            <TabsTrigger value="channels" data-testid="tab-channels" className="data-[state=active]:bg-emerald-600 flex-1 sm:flex-none text-xs sm:text-sm">Channels</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="data-[state=active]:bg-amber-500 data-[state=active]:text-gray-900 flex-1 sm:flex-none text-xs sm:text-sm">Offers</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className={`flex-1 sm:flex-none text-xs sm:text-sm ${user.is_admin && !user.twofa_enabled ? "data-[state=active]:bg-amber-600 text-amber-300" : "data-[state=active]:bg-emerald-600"}`}>
              Security{user.is_admin && !user.twofa_enabled ? " !" : ""}
            </TabsTrigger>
            {user.is_admin && (
              <TabsTrigger value="admin" data-testid="admin-tab" className="data-[state=active]:bg-amber-600 flex-1 sm:flex-none text-xs sm:text-sm">Admin</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="videos">
            <MyVideosTab
              videos={videos}
              selectedVideo={selectedVideo}
              setSelectedVideo={setSelectedVideo}
              videoComments={videoComments}
              loadingComments={loadingComments}
              onToggleHighlight={handleToggleHighlight}
              onDeleteVideo={handleDeleteVideo}
              onDeleteComment={handleDeleteComment}
              onRenewVideo={handleRenewVideo}
              isProducer={(user.subscription_tier || "free").toLowerCase() === "producer"}
              selfShareUsedToday={selfShareUsedToday}
              selfShareRemaining={selfShareRemaining}
              onSelfShared={handleSelfShared}
            />
          </TabsContent>

          <TabsContent value="chat">
            <ChannelChatTab
              messages={channelChatMessages}
              loading={loadingChannelChat}
              onDelete={handleDeleteChannelChat}
            />
          </TabsContent>

          <TabsContent value="transactions">
            <TransactionsTab transactions={transactions} />
          </TabsContent>

          <TabsContent value="profile">
            <ProfileTab user={user} token={token} refreshUser={refreshUser} />
          </TabsContent>

          <TabsContent value="channels">
            <ChannelsTab user={user} token={token} refreshUser={refreshUser} />
          </TabsContent>

          <TabsContent value="offers">
            <OffersTab token={token} refreshUser={refreshUser} />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab user={user} refreshUser={refreshUser} />
          </TabsContent>

          {user.is_admin && (
            <TabsContent value="admin">
              <Card className="bg-gray-900 border-amber-500/30">
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                    <Crown className="h-5 w-5 text-amber-400" />
                    Master Admin Console
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-xs sm:text-sm">
                    Site-wide analytics and controls now have a dedicated home.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <Link to="/admin">
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      <Crown className="h-4 w-4 mr-2" /> Open Admin Console
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* TV Station Banner */}
        <Card className="bg-gradient-to-r from-emerald-900/50 via-cyan-900/50 to-purple-900/50 border-emerald-500/30 mt-8">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Your TV Station</h3>
            </div>
            <p className="text-gray-300 text-sm sm:text-base">
              Register: <span className="text-emerald-400 font-semibold">www.{user.username}.TV</span>
            </p>
            <p className="text-gray-300 text-sm sm:text-base mb-6">
              Then forward it to: <span className="text-emerald-400 font-semibold">www.GlobalEyes.TV/{user.username}</span>
            </p>
            <a
              href="https://www.anrdoezrs.net/click-101577008-10730504"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8">
                <ExternalLink className="h-4 w-4 mr-2" /> HERE
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
      {/* Renewal dialog — controlled by `renewingVideo` state. */}
      <VideoRenewalDialog
        video={renewingVideo}
        userCredits={user?.credits}
        onClose={() => setRenewingVideo(null)}
        onRenewed={handleRenewed}
      />
      {/* Subscription success modal — listens for the
          'subscription-success' window event fired by
          usePaymentRedirectHandler after a successful checkout. */}
      <SubscriptionSuccessModal />
    </div>
  );
};

export default DashboardPage;
